'use client';

export const dynamic = 'force-dynamic';

import { useState } from 'react';
import {
	useRouter,
	useSearchParams,
} from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Image from 'next/image';
import { Eye, EyeOff } from 'lucide-react';
import { fetchUserServer } from '@/lib/fetchUserServer';

export default function LoginPage() {
	const router = useRouter();
	const { login } = useAuth();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [warnings, setWarnings] = useState<any[]>([]); // New state for warnings
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const params = useSearchParams();
	// Check for 'redirect' (from CartSidebar) or 'redirectTo' (legacy/other parts)
	// Do NOT default to /admin/dashboard, let AuthContext handle default routing via routeForRole
	const redirectDestination = params?.get('redirect') || params?.get('redirectTo') || undefined;

	const brand = {
		primary: '#051449',
		accent: '#FFCF23',
		soft: '#D2DFFF',
		bg: '#F8ECC1',
	};

	const validate = () => {
		if (!email.trim()) return 'Please enter your email';
		if (!/^\S+@\S+\.\S+$/.test(email))
			return 'Enter a valid email';
		if (!password) return 'Please enter your password';
		return null;
	};

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setWarnings([]); // Clear warnings
		const v = validate();
		if (v) return setError(v);

		setLoading(true);
		try {
			const res = await login(email, password, redirectDestination);
		} catch (err: any) {
			const msg = err?.response?.data?.message || err?.message || 'Login failed';
			setError(msg);

			// Check for warnings in response
			if (err?.response?.data?.warnings && Array.isArray(err.response.data.warnings)) {
				setWarnings(err.response.data.warnings);
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-[72vh] flex items-center justify-center px-4">
			<div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg">
				<div className="mb-6 text-center flex flex-col items-center">
					<div aria-hidden className="justify-center mb-2">
						<Image
							src={'/logo.jpg'}
							alt="Yunimall logo"
							width={50}
							height={45}
							style={{ objectFit: 'cover' }}
						/>
					</div>
					<h1
						className="text-2xl font-semibold"
						style={{ color: brand.primary }}
					>
						Welcome back
					</h1>
					<p className="text-sm text-gray-500">
						Sign in to your Yunimall account
					</p>
					<a href="/logistics/auth/login" className="mt-2 text-sm font-semibold text-[#051449] hover:underline">
						Rider? Sign in to your Rider Portal
					</a>
				</div>

				<form
					onSubmit={submit}
					className="space-y-4"
					noValidate
				>
					{error && (
						<div
							className="text-sm rounded p-2 bg-[#FFEFD5]"
							role="alert"
							style={{ border: `1px solid ${brand.soft}` }}
						>
							<strong
								className="block text-sm"
								style={{ color: '#5a3f00' }}
							>
								{error}
							</strong>
						</div>
					)}

					{/* Warnings Banner */}
					{warnings.length > 0 && (
						<div
							className="text-sm rounded p-3 bg-orange-50 border border-orange-200"
							role="alert"
						>
							<strong className="block text-orange-800 mb-1">Account Warnings:</strong>
							<ul className="list-disc pl-4 space-y-1">
								{warnings.map((w: any, i: number) => (
									<li key={i} className="text-orange-700 text-xs">
										{w.message} <span className="text-orange-400 text-[10px]">({new Date(w.date).toLocaleDateString()})</span>
									</li>
								))}
							</ul>
						</div>
					)}

					{/* Email field */}
					<div>
						<label
							htmlFor="email"
							className="block text-sm font-medium mb-1"
							style={{ color: brand.primary }}
						>
							Email address
						</label>
						<input
							id="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#D2DFFF]"
							placeholder="you@university.edu"
							style={{ borderColor: brand.soft }}
							required
						/>
					</div>

					{/* Password field with toggle */}
					<div>
						<label
							htmlFor="password"
							className="block text-sm font-medium mb-1"
							style={{ color: brand.primary }}
						>
							Password
						</label>
						<div className="relative">
							<input
								id="password"
								type={showPassword ? 'text' : 'password'}
								value={password}
								onChange={(e) =>
									setPassword(e.target.value)
								}
								className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#D2DFFF] pr-10"
								placeholder="Your password"
								style={{ borderColor: brand.soft }}
								required
							/>
							<button
								type="button"
								onClick={() =>
									setShowPassword(!showPassword)
								}
								className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
								tabIndex={-1}
							>
								{showPassword ? (
									<EyeOff size={18} />
								) : (
									<Eye size={18} />
								)}
							</button>
						</div>
					</div>

					{/* Remember & forgot */}
					<div className="flex items-center justify-between text-sm">
						<label className="flex items-center gap-2">
							<input
								type="checkbox"
								className="h-4 w-4 rounded border-gray-300"
							/>
							<span className="text-gray-600">
								Remember me
							</span>
						</label>
						<a
							href="/auth/forgot-password"
							className="text-sm font-medium"
							style={{ color: brand.primary }}
						>
							Forgot password?
						</a>
					</div>

					{/* Submit button */}
					<button
						type="submit"
						disabled={loading}
						className="w-full py-3 rounded-md font-semibold transition-transform disabled:opacity-70"
						style={{
							background: brand.accent,
							color: brand.primary,
							boxShadow: `0 8px 24px ${brand.soft}33`,
						}}
					>
						{loading ? 'Signing in...' : 'Sign in'}
					</button>

					{/* Register link */}
					<div className="text-center text-sm text-gray-600">
						Don’t have an account?{' '}
						<a
							href="/auth/register"
							className="font-semibold"
							style={{ color: brand.primary }}
						>
							Create account
						</a>
					</div>
				</form>
			</div>
		</div>
	);
}
